"use client";
import { motion, useMotionValue } from "framer-motion";
import Image from "next/image";
import { useTheme } from "next-themes";
import React, { useCallback } from "react";

import FlickeringGrid from "@/components/magicui/flickeringGrid";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/i18n";
import * as m from "@/paraglide/messages";

import Powered from "./powered";

const content = (m: any) => ({
  sectionLabel: m.portfolio_management_label(),
  title: m.portfolio_management_title(),
  description: m.portfolio_management_desc(),
  buttons: [
    {
      text: m.get_started(),
      href: "#pricing",
      variant: "default", // Assuming 'default' is a predefined variant in shadcn
    },
  ],
  imageSrc: "/path/to/dashboard-image.jpg", // Replace with your actual image path
  imageAlt: "Dashboard Preview",
});

// const assets = [
//   {
//     symbol: "btc",
//     name: "Bitcoin",
//     qty: 0.24,
//     price: 103698,
//     change: "+6.9%",
//     marketCap: "$1.15T",
//   },
//   {
//     symbol: "eth",
//     name: "Ethereum",
//     qty: 1.74,
//     price: 2806,
//     change: "+1.7%",
//     marketCap: "$367.28B",
//   },
//   {
//     symbol: "sol",
//     name: "Solana",
//     qty: 0.56,
//     price: 212,
//     change: "+10.2%",
//     marketCap: "$65.67B",
//   },
// ];

// const formatCurrency = (value: number) => {
//   return new Intl.NumberFormat("en-US", {
//     style: "currency",
//     currency: "USD",
//   }).format(value);
// };

// const calculatePortfolioTotal = (assets: any[]) => {
//   return assets.reduce((total, asset) => total + asset.qty * asset.price, 0);
// };

// const calculateTotalChange = (assets: any[]) => {
//   return assets
//     .reduce((total, asset) => {
//       const changePercentage = parseFloat(asset.change.replace("%", ""));
//       return total + asset.qty * asset.price * (changePercentage / 100);
//     }, 0)
//     .toFixed(2);
// };

const PortfolioFeature = () => {
  // const portfolioTotal = parseFloat(calculatePortfolioTotal(assets));
  // const totalChange = calculateTotalChange(assets);
  // const totalChangePercentage = ((totalChange / portfolioTotal) * 100).toFixed(
  //   2,
  // );
  const cardX = useMotionValue(0);
  const cardY = useMotionValue(0);
  // const rotateX = useTransform(cardY, [-300, 300], [10, -10]); // Reversed values
  // const rotateY = useTransform(cardX, [-300, 300], [-10, 10]); // Reversed values

  const handleMouseMove = useCallback(
    (event: any) => {
      const offsetX = event.clientX - window.innerWidth / 2;
      const offsetY = event.clientY - window.innerHeight / 2;

      cardX.set(offsetX);
      cardY.set(offsetY);
    },
    [cardX, cardY],
  );
  const { theme } = useTheme();
  const handleMouseLeave = useCallback(() => {
    cardX.set(0);
    cardY.set(0);
  }, [cardX, cardY]);
  return (
    <motion.div
      style={{
        perspective: 800,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        id="portfolio"
        className="relative scroll-pt-10 pt-16 lg:pt-24 pb-16 lg:pb-28 bg-foreground"
        style={{
          perspective: 800,
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="hidden absolute inset-x-0 -top-11 mt-[calc(-3/16*1rem)] lg:flex items-end">
          <div className="mr-[calc(-1*(theme(spacing.8)-theme(spacing[1.5])))] h-11 flex-auto bg-foreground"></div>
          <div className="mx-auto flex w-full justify-between px-6 sm:max-w-[40rem] md:max-w-[48rem] md:px-8 lg:max-w-[64rem] 0 xl:max-w-[80rem] text-foreground">
            <svg
              viewBox="0 0 56 48"
              aria-hidden="true"
              className="-ml-1.5 mb-[calc(-1/16*1rem)] w-14 flex-none overflow-visible fill-current"
            >
              <path d="M 2.686 3 H -4 V 48 H 56 V 47 H 53.314 A 8 8 0 0 1 47.657 44.657 L 8.343 5.343 A 8 8 0 0 0 2.686 3 Z"></path>
            </svg>
            <svg
              viewBox="0 0 56 48"
              aria-hidden="true"
              className="-mr-1.5 mb-[calc(-1/16*1rem)] w-14 flex-none overflow-visible fill-current"
            >
              <path d="M 53.314 3 H 60 V 48 H 0 V 47 H 2.686 A 8 8 0 0 0 8.343 44.657 L 47.657 5.343 A 8 8 0 0 1 53.314 3 Z"></path>
            </svg>
          </div>
          <div className="ml-[calc(-1*(theme(spacing.8)-theme(spacing[1.5])))] h-11 flex-auto bg-foreground"></div>
        </div>
        <FlickeringGrid
          className="[mask-image:radial-gradient(ellipse_at_top,white,transparent,transparent)] z-0 absolute  inset-0 size-full"
          squareSize={2}
          gridGap={8}
          color="#6B7280"
          maxOpacity={0.6}
          flickerChance={0.3}
          height={500}
        />
        <MaxWidthWrapper className="z-10 ">
          <section className=" flex flex-col items-center justify-between gap-8 lg:gap-2 lg:flex-row ">
            <motion.div className="flex flex-col items-center lg:items-start gap-4 lg:p-16 lg:w-7/12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                viewport={{ amount: 0.5, once: true }}
              >
                <Badge variant="secondary" className="shadow-sm z-10 ">
                  {content(m).sectionLabel}
                </Badge>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                viewport={{ amount: 0.5, once: true }}
              >
                <h2 className="text-transparent text-center lg:text-left text-2xl lg:text-4xl font-extrabold tracking-tight leading-normal  pb-4 text-balance bg-gradient-to-br from-white from-50% to-zinc-50/10 bg-clip-text dark:shadow-none dark:text-background shadow">
                  {content(m).title}
                </h2>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                viewport={{ amount: 0.5, once: true }}
              >
                <p className="mb-6 text-center lg:text-left text-balance text-white dark:text-background shadow-sm dark:shadow-none">
                  {content(m).description}
                </p>
              </motion.div>

              <motion.div
                className="flex space-x-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                viewport={{ amount: 0.5, once: true }}
              >
                {content(m).buttons.map((button, index) => (
                  <Button className="shadow-sm" key={index} variant="secondary">
                    <Link href={button.href}>{button.text}</Link>
                  </Button>
                ))}
              </motion.div>
            </motion.div>
            <div className="w-1/2 scale-150 origin-left">
              <div className="bg-gradient-to-b md:bg-gradient-to-r from-transparent to-foreground absolute w-full h-full"></div>
              {theme === "dark" ? (
                <Image
                  src={"/images/marketing/portfolio_dark.webp"}
                  alt="ARYA Crypto Dashboard Dark preview"
                  width="768"
                  height="814"
                  decoding="async"
                  data-nimg="1"
                  priority
                  loading="eager"
                  className=""
                  draggable={false}
                />
              ) : (
                <Image
                  src={"/images/marketing/portfolio_light.webp"}
                  alt="ARYA Crypto Dashboard preview"
                  width="768"
                  height="814"
                  decoding="async"
                  data-nimg="1"
                  priority
                  loading="eager"
                  className=""
                  draggable={false}
                />
              )}
            </div>
          </section>
          <Powered />
        </MaxWidthWrapper>
      </motion.div>

      <div className="mx-auto w-full px-6 sm:max-w-[40rem] md:max-w-[48rem] md:px-8 lg:max-w-[64rem] xl:max-w-[80rem]">
        <div className="relative -mx-2.5 flex -bottom-1 -mt-12">
          <svg
            viewBox="0 0 64 48"
            className="w-16 flex-none fill-white dark:fill-background"
            aria-hidden="true"
          >
            <path d="M51.657 2.343 12.343 41.657A8 8 0 0 1 6.686 44H0v4h64V0h-6.686a8 8 0 0 0-5.657 2.343Z"></path>
          </svg>
          <div className="-mx-px flex-auto bg-white dark:bg-background"></div>
          <svg
            viewBox="0 0 64 48"
            className="w-16 flex-none fill-white dark:fill-background"
            aria-hidden="true"
          >
            <path d="m12.343 2.343 39.314 39.314A8 8 0 0 0 57.314 44H64v4H0V0h6.686a8 8 0 0 1 5.657 2.343Z"></path>
          </svg>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioFeature;
